import { createFileRoute } from "@tanstack/react-router";
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Briefcase,
  Building2,
  Calendar,
  Car,
  
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Settings,
  UserPlus,
  Users,
  X,
  Download,
  Columns3,
} from "lucide-react";
import { confirmBookingAndEmail } from "@/lib/admin-bookings.functions";
import {
  assignBookingDriver,
  createAdminCompany,
  createAdminDriver,
  createAdminFleet,
  getAdminOverview,
  updateAdminBookingStatus,
} from "@/lib/admin-management.functions";
import type { Database } from "@/integrations/supabase/types";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

type Booking = Database["public"]["Tables"]["bookings"]["Row"];
type Driver = Database["public"]["Tables"]["admin_drivers"]["Row"];
type Fleet = Database["public"]["Tables"]["admin_fleets"]["Row"];
type Company = Database["public"]["Tables"]["admin_companies"]["Row"];

const STATUSES = ["pending", "confirmed", "completed", "cancelled"] as const;

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

function AdminDashboard() {
  const loadOverview = useServerFn(getAdminOverview);
  const confirmAndEmail = useServerFn(confirmBookingAndEmail);
  const updateStatusFn = useServerFn(updateAdminBookingStatus);
  const assignDriverFn = useServerFn(assignBookingDriver);
  const createDriverFn = useServerFn(createAdminDriver);
  const createFleetFn = useServerFn(createAdminFleet);
  const createCompanyFn = useServerFn(createAdminCompany);

  const [rows, setRows] = useState<Booking[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Booking | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [dateRange, setDateRange] = useState<"today" | "tomorrow" | "all" | "custom">("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortOrder, setSortOrder] = useState<"earliest" | "latest">("earliest");
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const [visibleCols, setVisibleCols] = useState({
    travel: true,
    customer: true,
    route: true,
    driver: true,
    price: true,
    status: true,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadOverview();
      setRows(data.bookings as Booking[]);
      setDrivers(data.drivers as Driver[]);
      setFleets(data.fleets as Fleet[]);
      setCompanies(data.companies as Company[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load admin dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const companyName = (id?: string | null) => companies.find((c) => c.id === id)?.name ?? "—";
  const driverName = (id?: string | null) => drivers.find((d) => d.id === id)?.name ?? "Unassigned";

  const filtered = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const tomorrowStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const result = rows.filter((b) => {
      if (status !== "all" && b.status !== status) return false;
      if (dateRange === "today" && b.travel_date !== todayStr) return false;
      if (dateRange === "tomorrow" && b.travel_date !== tomorrowStr) return false;
      if (dateRange === "custom") {
        if (from && b.travel_date < from) return false;
        if (to && b.travel_date > to) return false;
      }
      if (!q) return true;
      const needle = q.toLowerCase();
      return [
        b.customer_name,
        b.customer_email,
        b.customer_phone,
        b.pickup,
        b.dropoff,
        b.flight_number ?? "",
        b.id,
        driverName(b.driver_id),
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
    result.sort((a, b) => {
      const ka = `${a.travel_date} ${a.travel_time}`;
      const kb = `${b.travel_date} ${b.travel_time}`;
      return sortOrder === "earliest" ? ka.localeCompare(kb) : kb.localeCompare(ka);
    });
    return result;
  }, [rows, q, status, dateRange, from, to, sortOrder, drivers]);

  useEffect(() => {
    setVisibleCount(20);
  }, [q, status, dateRange, from, to, sortOrder]);

  const visibleRows = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  const exportCsv = () => {
    const headers = [
      "Date",
      "Time",
      "Customer",
      "Phone",
      "Email",
      "Pickup",
      "Dropoff",
      "Driver",
      "Price (MAD)",
      "Status",
    ];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    for (const b of filtered) {
      lines.push(
        [
          b.travel_date,
          b.travel_time,
          b.customer_name,
          b.customer_phone,
          b.customer_email,
          b.pickup,
          b.dropoff,
          driverName(b.driver_id),
          String(b.price_mad ?? ""),
          b.status,
        ]
          .map((v) => escape(String(v ?? "")))
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((b) => b.status === "pending").length;
    const confirmed = rows.filter((b) => b.status === "confirmed").length;
    const revenue = rows
      .filter((b) => b.status === "confirmed" || b.status === "completed")
      .reduce((sum, b) => sum + Number(b.price_mad || 0), 0);
    return { total, pending, confirmed, revenue };
  }, [rows]);

  const updateStatus = async (booking: Booking, next: string) => {
    setSaving(booking.id);
    const previous = rows;
    setRows((current) => current.map((b) => (b.id === booking.id ? { ...b, status: next } : b)));
    if (selected?.id === booking.id) setSelected({ ...selected, status: next });
    try {
      if (next === "confirmed") {
        await confirmAndEmail({ data: { bookingId: booking.id } });
      } else {
        await updateStatusFn({
          data: { bookingId: booking.id, status: next as (typeof STATUSES)[number] },
        });
      }
    } catch (e) {
      setRows(previous);
      if (selected?.id === booking.id) setSelected(booking);
      alert(`Update failed: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setSaving(null);
    }
  };

  const assignDriver = async (booking: Booking, driverId: string) => {
    const value = driverId || null;
    setSaving(`driver-${booking.id}`);
    try {
      await assignDriverFn({ data: { bookingId: booking.id, driverId: value } });
      setRows((current) =>
        current.map((b) => (b.id === booking.id ? { ...b, driver_id: value } : b)),
      );
      if (selected?.id === booking.id) setSelected({ ...selected, driver_id: value });
    } catch (e) {
      alert(`Driver assignment failed: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setSaving(null);
    }
  };

  const addCompany = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSaving("company");
    try {
      await createCompanyFn({
        data: {
          name: String(form.get("name") || ""),
          contact_name: String(form.get("contact_name") || ""),
          email: String(form.get("email") || ""),
          phone: String(form.get("phone") || ""),
          city: String(form.get("city") || ""),
          notes: String(form.get("notes") || ""),
        },
      });
      e.currentTarget.reset();
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Could not add company.");
    } finally {
      setSaving(null);
    }
  };

  const addDriver = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSaving("driver");
    try {
      await createDriverFn({
        data: {
          name: String(form.get("name") || ""),
          phone: String(form.get("phone") || ""),
          email: String(form.get("email") || ""),
          company_id: String(form.get("company_id") || ""),
          license_number: String(form.get("license_number") || ""),
          notes: String(form.get("notes") || ""),
        },
      });
      e.currentTarget.reset();
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Could not add driver.");
    } finally {
      setSaving(null);
    }
  };

  const addFleet = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSaving("fleet");
    try {
      await createFleetFn({
        data: {
          name: String(form.get("name") || ""),
          vehicle_type: String(form.get("vehicle_type") || ""),
          plate_number: String(form.get("plate_number") || ""),
          company_id: String(form.get("company_id") || ""),
          seats: Number(form.get("seats") || 4),
          luggage: Number(form.get("luggage") || 2),
          status: String(form.get("status") || "active") as "active" | "maintenance" | "inactive",
          notes: String(form.get("notes") || ""),
        },
      });
      e.currentTarget.reset();
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Could not add fleet.");
    } finally {
      setSaving(null);
    }
  };

  const clearFilters = () => {
    setQ("");
    setStatus("all");
    setDateRange("all");
    setFrom("");
    setTo("");
  };

  return (
    <div className="space-y-7">
      <section id="dashboard" className="space-y-1 scroll-mt-24">
        <h1 className="font-display text-3xl text-foreground tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Manage your journeys and operations</p>
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Total bookings" value={stats.total} />
        <StatCard label="Pending" value={stats.pending} accent="amber" />
        <StatCard label="Confirmed" value={stats.confirmed} accent="blue" />
        <StatCard label="Revenue (MAD)" value={stats.revenue.toLocaleString()} accent="green" />
        <StatCard label="Drivers" value={drivers.length} />
        <StatCard label="Fleet" value={fleets.length} />
      </section>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <section id="bookings" className="space-y-3 scroll-mt-24">
        <h2 className="font-display text-xl text-foreground">Journeys</h2>

        {/* Filter toolbar — matches Transferz layout */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented date tabs */}
          <div className="inline-flex rounded-lg border border-border bg-background p-0.5 shadow-sm">
            {(["today", "tomorrow", "all"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setDateRange(k)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize transition ${
                  dateRange === k
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                {k}
              </button>
            ))}
            <button
              onClick={() => setDateRange("custom")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition ${
                dateRange === "custom"
                  ? "bg-accent text-accent-foreground"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" /> Custom
            </button>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="admin-input pl-9 w-full"
            />
          </div>

          {/* Status dropdown */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="admin-input min-w-[130px]"
          >
            <option value="all">Status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "earliest" | "latest")}
            className="admin-input min-w-[170px]"
          >
            <option value="earliest">Pickup: Earliest first</option>
            <option value="latest">Pickup: Latest first</option>
          </select>
        </div>

        {/* Custom date inputs */}
        {dateRange === "custom" && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="admin-input"
            />
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="admin-input"
            />
          </div>
        )}

        {/* Utility row */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <Download className="h-3.5 w-3.5" /> Download Excel
          </button>
          <div className="relative">
            <button
              onClick={() => setColumnsOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
            >
              <Columns3 className="h-3.5 w-3.5" /> Columns
            </button>
            {columnsOpen && (
              <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border border-border bg-background p-2 shadow-lg">
                {(Object.keys(visibleCols) as Array<keyof typeof visibleCols>).map((k) => (
                  <label
                    key={k}
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-muted capitalize"
                  >
                    <input
                      type="checkbox"
                      checked={visibleCols[k]}
                      onChange={(e) =>
                        setVisibleCols({ ...visibleCols, [k]: e.target.checked })
                      }
                    />
                    {k}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground">
            Clear
          </button>
          <span className="text-xs text-muted-foreground ml-auto sm:ml-0">
            Showing {Math.min(visibleCount, filtered.length)} of {filtered.length}
          </span>
        </div>


        <div className="overflow-hidden rounded-2xl border border-border bg-background">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading admin data…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">
              No bookings match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {visibleCols.travel && <th className="px-3 py-2.5">Travel</th>}
                    {visibleCols.customer && <th className="px-3 py-2.5">Customer</th>}
                    {visibleCols.route && <th className="px-3 py-2.5">Route</th>}
                    {visibleCols.driver && <th className="px-3 py-2.5">Driver</th>}
                    {visibleCols.price && <th className="px-3 py-2.5 text-right">Price</th>}
                    {visibleCols.status && <th className="px-3 py-2.5">Status</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visibleRows.map((b) => (
                    <tr
                      key={b.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => setSelected(b)}
                    >
                      {visibleCols.travel && (
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-foreground">{b.travel_date}</div>
                          <div className="text-xs text-muted-foreground">{b.travel_time}</div>
                        </td>
                      )}
                      {visibleCols.customer && (
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-foreground">{b.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
                        </td>
                      )}
                      {visibleCols.route && (
                        <td className="max-w-[280px] px-3 py-2.5">
                          <div className="truncate text-foreground">{b.pickup}</div>
                          <div className="truncate text-xs text-muted-foreground">→ {b.dropoff}</div>
                        </td>
                      )}
                      {visibleCols.driver && (
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={b.driver_id ?? ""}
                            disabled={saving === `driver-${b.id}`}
                            onChange={(e) => assignDriver(b, e.target.value)}
                            className="admin-input min-w-40 py-1.5 text-xs"
                          >
                            <option value="">Add driver</option>
                            {drivers.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      {visibleCols.price && (
                        <td className="px-3 py-2.5 text-right font-medium text-foreground">
                          {Number(b.price_mad).toLocaleString()} MAD
                        </td>
                      )}
                      {visibleCols.status && (
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={b.status}
                            disabled={saving === b.id}
                            onChange={(e) => updateStatus(b, e.target.value)}
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium capitalize outline-none ${STATUS_COLORS[b.status] ?? "bg-muted text-foreground border-border"}`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {visibleCount < filtered.length && (
          <div className="flex justify-center pt-2">
            <button
              onClick={() => setVisibleCount((n) => n + 20)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              <Plus className="h-4 w-4" /> Load more
            </button>
          </div>
        )}
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-end bg-foreground/40 sm:items-stretch"
          onClick={() => setSelected(null)}
        >
          <div
            className="h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-background p-5 sm:h-full sm:max-w-md sm:rounded-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Booking</div>
                <div className="font-display text-xl text-foreground">
                  #{selected.id.slice(0, 8)}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="rounded-full p-1.5 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 text-sm">
              <Field icon={<Calendar className="h-4 w-4" />} label="Travel">
                {selected.travel_date} at {selected.travel_time} · {selected.trip_type}
              </Field>
              <Field icon={<MapPin className="h-4 w-4" />} label="Pickup">
                {selected.pickup}
              </Field>
              <Field icon={<MapPin className="h-4 w-4" />} label="Drop-off">
                {selected.dropoff}
              </Field>
              <Field icon={<Briefcase className="h-4 w-4" />} label="Vehicle">
                <span className="capitalize">
                  {selected.vehicle_type} · {selected.vehicle_class}
                </span>
              </Field>
              <Field icon={<UserPlus className="h-4 w-4" />} label="Driver">
                <select
                  value={selected.driver_id ?? ""}
                  onChange={(e) => assignDriver(selected, e.target.value)}
                  className="admin-input mt-1"
                >
                  <option value="">Add driver</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} · {d.phone}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Customer
                </div>
                <div className="mt-1 font-medium text-foreground">{selected.customer_name}</div>
                <a
                  href={`tel:${selected.customer_phone}`}
                  className="mt-1 flex items-center gap-1.5 text-foreground hover:text-primary"
                >
                  <Phone className="h-3.5 w-3.5" /> {selected.customer_phone}
                </a>
                <a
                  href={`mailto:${selected.customer_email}`}
                  className="mt-0.5 flex items-center gap-1.5 text-foreground hover:text-primary"
                >
                  <Mail className="h-3.5 w-3.5" /> {selected.customer_email}
                </a>
                {selected.flight_number && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Flight: {selected.flight_number}
                  </div>
                )}
              </div>
              {selected.notes && (
                <div className="rounded-lg bg-muted/50 p-3 text-xs text-foreground">
                  <div className="mb-1 font-medium uppercase tracking-wide text-muted-foreground">
                    Notes
                  </div>
                  {selected.notes}
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Total price
                </span>
                <span className="font-display text-lg text-foreground">
                  {Number(selected.price_mad).toLocaleString()} MAD
                </span>
              </div>
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Status
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      disabled={saving === selected.id}
                      onClick={() => updateStatus(selected, s)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition ${selected.status === s ? STATUS_COLORS[s] : "border-border bg-background text-muted-foreground hover:bg-muted"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <h2 className="flex items-center gap-2 font-display text-2xl text-foreground">
      {icon}
      {title}
    </h2>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: "amber" | "blue" | "green";
}) {
  const ring =
    accent === "amber"
      ? "ring-amber-200"
      : accent === "blue"
        ? "ring-blue-200"
        : accent === "green"
          ? "ring-green-200"
          : "ring-border";
  return (
    <div className={`rounded-2xl border border-border bg-background p-4 ring-1 ${ring}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl text-foreground">{value}</div>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl text-foreground">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border pb-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function ManagementPanel({
  id,
  icon,
  title,
  onSubmit,
  busy,
  children,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 rounded-2xl border border-border bg-background p-5">
      <SectionTitle icon={icon} title={title} />
      <form onSubmit={onSubmit} className="mt-4 space-y-3">
        {children}
        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save
        </button>
      </form>
    </section>
  );
}

function Input({ label, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block text-xs font-medium text-muted-foreground">
      {label}
      <input {...props} className="admin-input mt-1" />
    </label>
  );
}

function Textarea({
  label,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string }) {
  return (
    <label className="block text-xs font-medium text-muted-foreground">
      {label}
      <textarea {...props} rows={2} className="admin-input mt-1 resize-none" />
    </label>
  );
}

function Select({
  label,
  options,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block text-xs font-medium text-muted-foreground">
      {label}
      <select {...props} className="admin-input mt-1">
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function List({ items, empty }: { items: string[]; empty: string }) {
  return (
    <div className="rounded-xl bg-muted/30 p-3 text-xs text-muted-foreground">
      <div className="mb-2 font-medium text-foreground">Saved</div>
      {items.length === 0 ? (
        empty
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 5).map((item) => (
            <li key={item} className="truncate">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </div>
      <div className="mt-1 text-foreground">{children}</div>
    </div>
  );
}
